import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { Select, Input } from 'antd';
import { StarFilled, StarOutlined } from '@ant-design/icons';
import chatStore from '../stores/chatStore';
import { formatAvgImageCost, formatModelPricing } from '../utils/money';
import { PROMPT_MOBILE_MAX_WIDTH } from '../utils/promptTextarea';
import { useTheme } from '../theme/ThemeContext';
import ModelModalityIcons, { MODALITY_META, MODALITY_ORDER } from './ModelModalityIcons';
import ModelResolutionIcons from './ModelResolutionIcons';
import { ModelParamBadges } from './ModelParamBadges';
import BrainIcon from './BrainIcon';
import { modelHasReasoning } from '../utils/reasoning';
import { getModelAspectValues, resolveAspectBadgeItems } from '../utils/aspectRatios';
import { getModelResolutionValues, getModelNMax, modelSupportsResolution } from '../utils/imageSize';
import { audioKind } from '../utils/mediaParams';

const AUDIO_KIND_BADGE = {
    tts: 'Озвучка',
    stt: 'Расшифровка',
    music: 'Музыка',
};

function formatPrice(pricing) {
    return formatModelPricing(pricing, chatStore.settings);
}

const RESOLUTION_ORDER = ['512', '1K', '2K', '4K'];

function sortResolutionValues(values) {
    return [...values].sort((a, b) => {
        const ai = RESOLUTION_ORDER.indexOf(a);
        const bi = RESOLUTION_ORDER.indexOf(b);
        if (ai === -1 && bi === -1) return String(a).localeCompare(String(b));
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });
}

function useIsMobileViewport(maxWidth = PROMPT_MOBILE_MAX_WIDTH) {
    const [mobile, setMobile] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
        const apply = () => setMobile(mq.matches);
        apply();
        if (mq.addEventListener) {
            mq.addEventListener('change', apply);
            return () => mq.removeEventListener('change', apply);
        }
        mq.addListener(apply);
        return () => mq.removeListener(apply);
    }, [maxWidth]);

    return mobile;
}

function ModelListBody({
    imageMode,
    search,
    setSearch,
    sortedModels,
    favoriteSet,
    favoriteCount,
    value,
    selectModel,
    isDark,
    aspectFilter,
    setAspectFilter,
    resolutionFilter,
    setResolutionFilter,
    nMaxFilter,
    setNMaxFilter,
    inputModalityFilter,
    setInputModalityFilter,
    aspectFilterOptions,
    resolutionFilterOptions,
    nMaxFilterOptions,
    inputModalityFilterOptions,
    listKind,
    audioKindFilter,
    setAudioKindFilter,
    audioKindFilterOptions,
}) {
    const hasImageFilters =
        imageMode &&
        (aspectFilterOptions.length > 0 ||
            resolutionFilterOptions.length > 0 ||
            nMaxFilterOptions.length > 0);
    const hasChatFilters = listKind === 'chat' && inputModalityFilterOptions.length > 0;
    const hasAudioFilters = listKind === 'audio' && (audioKindFilterOptions || []).length > 0;

    const sectionLabel = isDark
        ? 'mb-0.5 text-[0.7em] uppercase tracking-wide text-white/55'
        : 'mb-0.5 text-[0.7em] uppercase tracking-wide text-black/45';
    const mutedHint = isDark ? 'text-white/55' : 'text-black/50';
    const listHeader = isDark
        ? 'px-3 py-1 text-[0.75em] uppercase tracking-wide text-white/50'
        : 'px-3 py-1 text-[0.75em] uppercase tracking-wide text-black/40';

    return (
        <div
            className={`bananas-model-dropdown flex min-h-0 flex-1 flex-col ${
                isDark ? 'text-gray-100' : 'text-gray-900'
            }`}
        >
            <div
                className={`shrink-0 border-b p-2 ${
                    isDark ? 'border-white/10' : 'border-black/5'
                }`}
            >
                <Input
                    size="small"
                    className={`bananas-model-search ${isDark ? 'bananas-model-search--dark' : ''}`}
                    placeholder="Поиск модели"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                    autoFocus
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                {hasImageFilters && (
                    <div className="mt-1.5 max-h-[140px] space-y-1.5 overflow-y-auto px-0.5">
                        {aspectFilterOptions.length > 0 && (
                            <div>
                                <div className={sectionLabel}>Формат</div>
                                <ModelParamBadges
                                    values={aspectFilterOptions}
                                    isDark={isDark}
                                    selected={aspectFilter}
                                    onSelect={setAspectFilter}
                                />
                            </div>
                        )}
                        {resolutionFilterOptions.length > 0 && (
                            <div>
                                <div className={sectionLabel}>Качество</div>
                                <ModelParamBadges
                                    values={resolutionFilterOptions}
                                    isDark={isDark}
                                    selected={resolutionFilter}
                                    onSelect={setResolutionFilter}
                                />
                            </div>
                        )}
                        {nMaxFilterOptions.length > 0 && (
                            <div>
                                <div className={sectionLabel}>Кол-во</div>
                                <ModelParamBadges
                                    values={nMaxFilterOptions}
                                    isDark={isDark}
                                    selected={nMaxFilter}
                                    onSelect={setNMaxFilter}
                                />
                            </div>
                        )}
                    </div>
                )}
                {hasChatFilters && (
                    <div className="mt-1.5 space-y-1.5 px-0.5">
                        <div>
                            <div className={sectionLabel}>Вход</div>
                            <ModelParamBadges
                                values={inputModalityFilterOptions}
                                isDark={isDark}
                                selected={inputModalityFilter}
                                onSelect={setInputModalityFilter}
                            />
                        </div>
                    </div>
                )}
                {hasAudioFilters && (
                    <div className="mt-1.5 space-y-1.5 px-0.5">
                        <div>
                            <div className={sectionLabel}>Тип</div>
                            <ModelParamBadges
                                values={audioKindFilterOptions}
                                isDark={isDark}
                                selected={audioKindFilter}
                                onSelect={setAudioKindFilter}
                            />
                        </div>
                    </div>
                )}
                {favoriteCount > 0 &&
                    !search.trim() &&
                    !aspectFilter &&
                    !resolutionFilter &&
                    !nMaxFilter &&
                    !inputModalityFilter && (
                    <div className={`mt-1.5 px-0.5 text-[0.85em] ${mutedHint}`}>
                        ★ Избранное сверху · {favoriteCount}
                    </div>
                )}
            </div>

            <div
                className={`bananas-model-list-scroll min-h-[140px] flex-1 overflow-y-auto overflow-x-hidden py-1 ${
                    isDark ? 'bananas-model-list-scroll--dark' : ''
                }`}
            >
                {sortedModels.length === 0 && (
                    <div className={`px-3 py-4 text-center text-xs ${mutedHint}`}>
                        Модели не найдены
                    </div>
                )}

                {sortedModels.map((m, index) => {
                    const isFavorite = favoriteSet.has(m.id);
                    const isSelected = m.id === value;
                    const showFavDivider =
                        !search.trim() &&
                        !aspectFilter &&
                        !resolutionFilter &&
                        !nMaxFilter &&
                        !inputModalityFilter &&
                        favoriteCount > 0 &&
                        index === favoriteCount;
                    const avgCostLabel = imageMode
                        ? formatAvgImageCost(
                              (chatStore.settings.image_model_stats || {})[m.id],
                              chatStore.settings
                          )
                        : null;
                    const aspectValues = imageMode
                        ? getModelAspectValues(m.supported_parameters || {})
                        : null;
                    const aspectBadges = aspectValues?.length
                        ? resolveAspectBadgeItems(
                              aspectValues,
                              chatStore.settings.aspect_ratios
                          )
                        : [];

                    return (
                        <React.Fragment key={m.id}>
                            {showFavDivider && (
                                <div
                                    className={`my-1 border-t ${listHeader} ${
                                        isDark ? 'border-white/10' : 'border-black/5'
                                    }`}
                                >
                                    Все модели
                                </div>
                            )}
                            {!search.trim() &&
                                !aspectFilter &&
                                !resolutionFilter &&
                                !nMaxFilter &&
                                !inputModalityFilter &&
                                index === 0 &&
                                favoriteCount > 0 && (
                                <div className={listHeader}>Избранное</div>
                            )}
                            <div
                                className={`flex cursor-pointer items-start gap-2 px-3 py-2.5 ${
                                    isSelected
                                        ? 'bg-emerald-500/15'
                                        : isDark
                                          ? 'hover:bg-white/5'
                                          : 'hover:bg-black/[0.04]'
                                }`}
                                onClick={() => selectModel(m.id)}
                            >
                                <button
                                    type="button"
                                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-[1.15em] leading-none ${
                                        isFavorite
                                            ? 'text-amber-400'
                                            : isDark
                                              ? 'text-white/40 hover:text-amber-400'
                                              : 'text-black/30 hover:text-amber-400'
                                    }`}
                                    title={isFavorite ? 'Убрать из избранного' : 'В избранное'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        chatStore.toggleFavoriteModel(m.id, listKind || (imageMode ? 'image' : 'chat'));
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    {isFavorite ? <StarFilled /> : <StarOutlined />}
                                </button>

                                <div className="min-w-0 flex-1 overflow-hidden">
                                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                                        <span
                                            className={`min-w-0 flex-1 truncate font-medium ${
                                                isDark ? 'text-gray-100' : 'text-gray-900'
                                            }`}
                                        >
                                            {m.name || m.id}
                                        </span>
                                        {listKind === 'audio' && AUDIO_KIND_BADGE[audioKind(m)] ? (
                                            <span
                                                className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                                                    isDark
                                                        ? 'bg-white/10 text-white/70'
                                                        : 'bg-black/[0.06] text-black/55'
                                                }`}
                                            >
                                                {AUDIO_KIND_BADGE[audioKind(m)]}
                                            </span>
                                        ) : null}
                                        <span className="inline-flex max-w-full flex-wrap items-center gap-0.5">
                                            {modelHasReasoning(m) ? (
                                                <span
                                                    className={`inline-flex shrink-0 ${
                                                        isDark ? 'text-white/60' : 'text-black/40'
                                                    }`}
                                                    title="Поддерживает reasoning"
                                                >
                                                    <BrainIcon size={14} title="Reasoning" />
                                                </span>
                                            ) : null}
                                            {imageMode ? (
                                                <ModelResolutionIcons
                                                    supportedParameters={m.supported_parameters}
                                                    isDark={isDark}
                                                />
                                            ) : null}
                                            <ModelModalityIcons
                                                inputModalities={m.input_modalities}
                                                outputModalities={m.output_modalities}
                                                isDark={isDark}
                                            />
                                        </span>
                                    </div>
                                    <div
                                        className={`flex min-w-0 items-center gap-1.5 text-[0.85em] ${
                                            isDark ? 'text-white/55' : 'text-black/55'
                                        }`}
                                    >
                                        <span className="min-w-0 truncate">{m.id}</span>
                                        {avgCostLabel ? (
                                            <span
                                                className="shrink-0 truncate"
                                                title="Средняя стоимость одного изображения по вашим генерациям"
                                            >
                                                · {avgCostLabel}
                                            </span>
                                        ) : null}
                                        {!imageMode && formatPrice(m.pricing) ? (
                                            <span className="shrink-0 truncate">
                                                · {formatPrice(m.pricing)}
                                            </span>
                                        ) : null}
                                    </div>
                                    {aspectBadges.length ? (
                                        <div className="mt-0.5 min-w-0">
                                            <ModelParamBadges
                                                values={aspectBadges}
                                                isDark={isDark}
                                                title={`Поддерживаемые форматы: ${aspectBadges
                                                    .map((i) =>
                                                        i.label !== i.value
                                                            ? `${i.label} (${i.value})`
                                                            : i.value
                                                    )
                                                    .join(', ')}`}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

const ModelSelect = observer(({ imageMode = false, kind }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [aspectFilter, setAspectFilter] = useState(null);
    const [resolutionFilter, setResolutionFilter] = useState(null);
    const [nMaxFilter, setNMaxFilter] = useState(null);
    const [inputModalityFilter, setInputModalityFilter] = useState(null);
    const [audioKindFilter, setAudioKindFilter] = useState(null);
    const isMobile = useIsMobileViewport();
    const listKind = kind || (imageMode ? 'image' : 'chat');
    const models =
        listKind === 'image'
            ? chatStore.imageModels
            : listKind === 'audio'
              ? chatStore.audioModels
              : listKind === 'video'
                ? chatStore.videoModels
                : chatStore.models;
    const value =
        listKind === 'image'
            ? chatStore.settings.selected_image_model
            : listKind === 'audio'
              ? chatStore.settings.selected_audio_model
              : listKind === 'video'
                ? chatStore.settings.selected_video_model
                : chatStore.settings.selected_model;
    const favorites =
        listKind === 'image'
            ? chatStore.settings.favorite_image_models || []
            : listKind === 'audio'
              ? chatStore.settings.favorite_audio_models || []
              : listKind === 'video'
                ? chatStore.settings.favorite_video_models || []
                : chatStore.settings.favorite_models || [];
    const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

    const aspectFilterOptions = useMemo(() => {
        if (listKind !== 'image') return [];
        const seen = new Set();
        for (const m of models || []) {
            const vals = getModelAspectValues(m.supported_parameters || {});
            if (!vals) continue;
            for (const ratio of vals) seen.add(ratio);
        }
        return resolveAspectBadgeItems(
            [...seen].sort((a, b) => a.localeCompare(b, 'ru')),
            chatStore.settings.aspect_ratios
        );
    }, [listKind, models, chatStore.settings.aspect_ratios]);

    const resolutionFilterOptions = useMemo(() => {
        if (listKind !== 'image') return [];
        const seen = new Set();
        for (const m of models || []) {
            for (const tier of getModelResolutionValues(m.supported_parameters || {})) {
                seen.add(tier);
            }
        }
        return sortResolutionValues([...seen]);
    }, [listKind, models]);

    const nMaxFilterOptions = useMemo(() => {
        if (listKind !== 'image') return [];
        const seen = new Set();
        for (const m of models || []) {
            seen.add(getModelNMax(m.supported_parameters || {}));
        }
        const values = [...seen].filter((v) => v > 1).sort((a, b) => a - b);
        if (!values.length) return [];
        return values.map((max) => ({
            value: String(max),
            label: `×${max}`,
        }));
    }, [listKind, models]);

    const inputModalityFilterOptions = useMemo(() => {
        if (listKind !== 'chat') return [];
        const seen = new Set();
        for (const m of models || []) {
            for (const mod of m.input_modalities || []) {
                const key = String(mod || '').toLowerCase();
                if (MODALITY_META[key]) seen.add(key);
            }
        }
        return MODALITY_ORDER.filter((key) => seen.has(key)).map((key) => ({
            value: key,
            label: MODALITY_META[key].label,
        }));
    }, [listKind, models]);

    const audioKindFilterOptions = useMemo(() => {
        if (listKind !== 'audio') return [];
        const seen = new Set();
        for (const m of models || []) {
            const kind = audioKind(m);
            if (kind) seen.add(kind);
        }
        return [
            { value: 'tts', label: 'Озвучка' },
            { value: 'stt', label: 'Расшифровка' },
            { value: 'music', label: 'Музыка' },
        ].filter((item) => seen.has(item.value));
    }, [listKind, models]);

    const sortedModels = useMemo(() => {
        const q = search.trim().toLowerCase();
        const nMaxNeed = nMaxFilter != null ? Number(nMaxFilter) : null;
        const filtered = (models || []).filter((m) => {
            if (q) {
                const hit =
                    (m.name || '').toLowerCase().includes(q) ||
                    (m.id || '').toLowerCase().includes(q);
                if (!hit) return false;
            }
            if (listKind === 'image' && aspectFilter) {
                const vals = getModelAspectValues(m.supported_parameters || {});
                if (vals?.length && !vals.includes(aspectFilter)) return false;
            }
            if (listKind === 'image' && resolutionFilter) {
                if (!modelSupportsResolution(m.supported_parameters || {}, resolutionFilter)) {
                    return false;
                }
            }
            if (listKind === 'image' && nMaxNeed != null && Number.isFinite(nMaxNeed)) {
                if (getModelNMax(m.supported_parameters || {}) < nMaxNeed) return false;
            }
            if (listKind === 'chat' && inputModalityFilter) {
                const inputs = (m.input_modalities || []).map((x) => String(x).toLowerCase());
                if (!inputs.includes(inputModalityFilter)) return false;
            }
            if (listKind === 'audio' && audioKindFilter) {
                if (audioKind(m) !== audioKindFilter) return false;
            }
            return true;
        });

        const favOrder = new Map(favorites.map((id, idx) => [id, idx]));
        filtered.sort((a, b) => {
            const af = favOrder.has(a.id);
            const bf = favOrder.has(b.id);
            if (af && bf) return favOrder.get(a.id) - favOrder.get(b.id);
            if (af) return -1;
            if (bf) return 1;
            return (a.name || a.id || '').localeCompare(b.name || b.id || '', 'ru');
        });

        return filtered.slice(0, 250);
    }, [
        models,
        search,
        favorites,
        listKind,
        aspectFilter,
        resolutionFilter,
        nMaxFilter,
        inputModalityFilter,
        audioKindFilter,
    ]);

    const clearFilters = () => {
        setSearch('');
        setAspectFilter(null);
        setResolutionFilter(null);
        setNMaxFilter(null);
        setInputModalityFilter(null);
        setAudioKindFilter(null);
    };

    const selectModel = (modelId) => {
        const field =
            listKind === 'image'
                ? 'selected_image_model'
                : listKind === 'audio'
                  ? 'selected_audio_model'
                  : listKind === 'video'
                    ? 'selected_video_model'
                    : 'selected_model';
        chatStore.saveSettings({ [field]: modelId });
        setOpen(false);
        clearFilters();
    };

    useEffect(() => {
        if (!open) return undefined;
        if (listKind === 'audio' && !chatStore.audioModels.length) {
            chatStore.loadAudioModels().catch(() => {});
        }
        if (listKind === 'video' && !chatStore.videoModels.length) {
            chatStore.loadVideoModels().catch(() => {});
        }
        return undefined;
    }, [open, listKind]);

    useEffect(() => {
        if (!open || !isMobile) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open, isMobile]);

    const favoriteCount = sortedModels.filter((m) => favoriteSet.has(m.id)).length;
    const selectedModel = (models || []).find((m) => m.id === value);
    const selectedLabel = selectedModel?.name || value || undefined;
    const selectedHasReasoning = listKind === 'chat' && modelHasReasoning(selectedModel);
    const modelPlaceholder =
        listKind === 'image'
            ? 'Модель изображений'
            : listKind === 'audio'
              ? 'Аудиомодель'
              : listKind === 'video'
                ? 'Модель видео'
                : 'Модель';

    const listProps = {
        imageMode: listKind === 'image',
        listKind,
        search,
        setSearch,
        sortedModels,
        favoriteSet,
        favoriteCount,
        value,
        selectModel,
        isDark,
        aspectFilter,
        setAspectFilter,
        resolutionFilter,
        setResolutionFilter,
        nMaxFilter,
        setNMaxFilter,
        inputModalityFilter,
        setInputModalityFilter,
        aspectFilterOptions,
        resolutionFilterOptions,
        nMaxFilterOptions,
        inputModalityFilterOptions,
        audioKindFilter,
        setAudioKindFilter,
        audioKindFilterOptions,
    };

    if (isMobile) {
        return (
            <>
                <button
                    type="button"
                    className={`bananas-model-select flex h-[32px] w-full items-center justify-between gap-2 rounded-md border px-3 text-left text-sm ${
                        isDark
                            ? 'border-white/20 bg-[#141414] text-gray-100'
                            : 'border-black/15 bg-white text-gray-900'
                    }`}
                    onClick={() => setOpen(true)}
                >
                    <span className="flex min-w-0 flex-1 items-center gap-1.5">
                        <span className="min-w-0 flex-1 truncate">
                            {selectedLabel || modelPlaceholder}
                        </span>
                        {selectedHasReasoning ? (
                            <span
                                className={`inline-flex shrink-0 ${
                                    isDark ? 'text-white/50' : 'text-black/40'
                                }`}
                            >
                                <BrainIcon size={14} title="Reasoning" />
                            </span>
                        ) : null}
                    </span>
                    <span className="shrink-0 opacity-45">▾</span>
                </button>
                {open &&
                    createPortal(
                        <div
                            className="fixed inset-0 z-[11000] flex flex-col bg-black/55 p-3 pt-10"
                            onClick={() => {
                                setOpen(false);
                                clearFilters();
                            }}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Выбор модели"
                        >
                            <div
                                className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl shadow-2xl ${
                                    isDark ? 'bg-[#1f1f1f] text-gray-100' : 'bg-white text-gray-900'
                                }`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    className={`flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5 ${
                                        isDark ? 'border-white/10' : 'border-black/8'
                                    }`}
                                >
                                    <span className="text-sm font-semibold">
                                        {modelPlaceholder}
                                    </span>
                                    <button
                                        type="button"
                                        className={`rounded-lg px-2.5 py-1 text-sm ${
                                            isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                                        }`}
                                        onClick={() => {
                                            setOpen(false);
                                            clearFilters();
                                        }}
                                    >
                                        Закрыть
                                    </button>
                                </div>
                                <ModelListBody {...listProps} />
                            </div>
                        </div>,
                        document.body
                    )}
            </>
        );
    }

    return (
        <Select
            open={open}
            onDropdownVisibleChange={(next) => {
                setOpen(next);
                if (!next) clearFilters();
            }}
            value={value || undefined}
            placeholder={modelPlaceholder}
            className={`w-full bananas-model-select ${
                isDark
                    ? '[&_.ant-select-selector]:!bg-white/5 [&_.ant-select-selector]:!border-white/12 [&_.ant-select-selector]:!shadow-none [&_.ant-select-selection-item]:!text-gray-100 [&_.ant-select-selection-placeholder]:!text-white/40 [&_.ant-select-arrow]:!text-white/45'
                    : ''
            }`}
            popupMatchSelectWidth
            getPopupContainer={() => document.body}
            dropdownStyle={{
                padding: 0,
                zIndex: 11000,
                ...(isDark
                    ? {
                          background: '#1f1f1f',
                          border: '1px solid rgba(255,255,255,0.1)',
                      }
                    : {}),
            }}
            dropdownClassName={`bananas-model-select-dropdown ${
                isDark ? 'bananas-model-select-dropdown--dark' : ''
            }`}
            options={
                value
                    ? [
                          {
                              value,
                              label: (
                                  <span className="flex min-w-0 items-center gap-1.5">
                                      <span className="min-w-0 flex-1 truncate">
                                          {selectedLabel || value}
                                      </span>
                                      {selectedHasReasoning ? (
                                          <span
                                              className={`inline-flex shrink-0 ${
                                                  isDark ? 'text-white/50' : 'text-black/40'
                                              }`}
                                          >
                                              <BrainIcon size={13} title="Reasoning" />
                                          </span>
                                      ) : null}
                                  </span>
                              ),
                          },
                      ]
                    : []
            }
            dropdownRender={() => (
                <div
                    className={`flex max-h-[380px] flex-col ${
                        isDark ? 'bg-[#1f1f1f] text-gray-100' : 'bg-white text-gray-900'
                    }`}
                >
                    <ModelListBody {...listProps} />
                </div>
            )}
        />
    );
});

export default ModelSelect;
